import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Search, 
  FileText, 
  CheckCircle, 
  AlertTriangle, 
  Plus,
  ArrowUpRight,
  Calendar,
  Download,
  FileSpreadsheet,
  Gavel
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '../config';

const MONTHS = [
  'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu',
  'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => currentYear - i);

export default function HistoryPage() {
  const navigate = useNavigate();
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [yearFilter, setYearFilter] = useState('all');
  const [selectedPayslips, setSelectedPayslips] = useState([]);

  useEffect(() => {
    fetchPayslips();
  }, [yearFilter]);

  const fetchPayslips = async () => {
    setLoading(true);
    try {
      const params = yearFilter !== 'all' ? `?year=${yearFilter}` : '';
      const response = await axios.get(`${API_URL}/payslips${params}`);
      setPayslips(response.data);
    } catch (err) {
      console.error('Error fetching payslips:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToVertenza = async () => {
    if (selectedPayslips.length === 0) return;
    
    // Pick the first selected payslip to get initial metadata (e.g. employee, company, ccnl)
    const firstPs = payslips.find(p => p.id === selectedPayslips[0]);
    const lavoratore = firstPs?.employee_name || 'Lavoratore';
    const datore = firstPs?.company_name || 'Azienda';
    
    const titolo = prompt('Inserisci il titolo della pratica di vertenza:', `Vertenza ${lavoratore} vs ${datore}`);
    if (titolo === null) return; // User cancelled
    
    try {
      const payload = {
        payslip_ids: selectedPayslips,
        titolo: titolo || `Vertenza ${lavoratore} vs ${datore}`,
        lavoratoreNome: lavoratore,
        datoreNome: datore,
        ccnlCodice: 'commercio', // default fallback
        ccnlNome: firstPs?.ccnl_name || 'Terziario, Distribuzione e Servizi',
        livello: '4',
        livelloApplicato: '5'
      };
      
      const response = await axios.post(`${API_URL}/sindacato/vertenza/convert-payslips`, payload);
      toast.success('Pratica di vertenza creata con successo dai cedolini selezionati!');
      setSelectedPayslips([]);
      navigate(`/vertenza/${response.data.id}`);
    } catch (err) {
      console.error(err);
      toast.error('Errore durante la conversione in vertenza.');
    }
  };

  const toggleSelectAll = () => {
    if (selectedPayslips.length === filteredPayslips.length) {
      setSelectedPayslips([]);
    } else {
      setSelectedPayslips(filteredPayslips.map(p => p.id));
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedPayslips(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleExportExcel = async () => {
    try {
      const params = yearFilter !== 'all' ? `?year=${yearFilter}` : '';
      const response = await axios.get(`${API_URL}/export/excel${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `buste_paga_${yearFilter !== 'all' ? yearFilter : 'tutte'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel scaricato');
    } catch (err) {
      toast.error('Errore nel download Excel');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const filteredPayslips = payslips.filter(p => 
    p.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.company_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="history-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Storico Buste Paga</h1>
            <p className="text-muted-foreground mt-1">Tutte le tue buste paga verificate</p>
          </div>
          <div className="flex items-center gap-2">
            {selectedPayslips.length > 0 && (
              <Button 
                onClick={handleConvertToVertenza}
                className="bg-forest text-white hover:bg-forest/90 font-medium"
              >
                <Gavel className="w-4 h-4 mr-2" />
                Apri Vertenza ({selectedPayslips.length})
              </Button>
            )}
            <Button 
              variant="outline" 
              className="swiss-btn-secondary"
              onClick={handleExportExcel}
              disabled={payslips.length === 0}
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel
            </Button>
            <Button asChild className="swiss-btn-primary">
              <Link to="/upload" data-testid="new-payslip-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuova Busta
              </Link>
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="swiss-card">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Cerca per nome o azienda..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="swiss-input pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger className="swiss-input w-full sm:w-[180px]" data-testid="year-filter">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtra per anno" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli anni</SelectItem>
                  {YEARS.map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Payslips List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner" />
          </div>
        ) : filteredPayslips.length === 0 ? (
          <div className="empty-state py-16">
            <FileText className="empty-state-icon" />
            <h3 className="text-lg font-semibold mb-2">Nessuna busta paga trovata</h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm || yearFilter !== 'all' 
                ? 'Prova a modificare i filtri di ricerca'
                : 'Inizia caricando la tua prima busta paga'}
            </p>
            {!searchTerm && yearFilter === 'all' && (
              <Button asChild className="swiss-btn-primary">
                <Link to="/upload">
                  <Plus className="w-4 h-4 mr-2" />
                  Carica Busta Paga
                </Link>
              </Button>
            )}
          </div>
        ) : (
          <Card className="swiss-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="payslip-table">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="w-12 text-center">
                      <input 
                        type="checkbox" 
                        checked={filteredPayslips.length > 0 && selectedPayslips.length === filteredPayslips.length}
                        onChange={toggleSelectAll}
                        className="rounded border-slate-300 text-forest focus:ring-forest"
                      />
                    </th>
                    <th>Periodo</th>
                    <th>Dipendente</th>
                    <th>Azienda</th>
                    <th className="text-right">Lordo</th>
                    <th className="text-right">Netto</th>
                    <th className="text-center">Stato</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayslips.map((payslip) => (
                    <tr key={payslip.id} data-testid={`payslip-row-${payslip.id}`}>
                      <td className="text-center">
                        <input 
                          type="checkbox" 
                          checked={selectedPayslips.includes(payslip.id)}
                          onChange={() => toggleSelectRow(payslip.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded border-slate-300 text-forest focus:ring-forest"
                        />
                      </td>
                      <td>
                        <span className="font-medium">
                          {MONTHS[payslip.period_month - 1]} {payslip.period_year}
                        </span>
                      </td>
                      <td>{payslip.employee_name}</td>
                      <td className="text-muted-foreground">{payslip.company_name}</td>
                      <td className="text-right font-mono">{formatCurrency(payslip.gross_salary)}</td>
                      <td className="text-right font-mono font-medium text-forest">
                        {formatCurrency(payslip.net_salary)}
                      </td>
                      <td className="text-center">
                        {payslip.is_valid ? (
                          <span className="valid-badge">
                            <CheckCircle className="w-3 h-3" /> Verificata
                          </span>
                        ) : (
                          <span className="invalid-badge">
                            <AlertTriangle className="w-3 h-3" /> {payslip.total_issues} anomalie
                          </span>
                        )}
                      </td>
                      <td>
                        <Button asChild variant="ghost" size="sm">
                          <Link to={`/payslip/${payslip.id}`} data-testid={`view-payslip-${payslip.id}`}>
                            <ArrowUpRight className="w-4 h-4" />
                          </Link>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Summary */}
        {filteredPayslips.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-4 text-sm text-muted-foreground">
            <span>
              Totale: <strong className="text-foreground">{filteredPayslips.length}</strong> buste paga
            </span>
            <span>
              Verificate: <strong className="text-emerald-600">{filteredPayslips.filter(p => p.is_valid).length}</strong>
            </span>
            <span>
              Con anomalie: <strong className="text-amber-600">{filteredPayslips.filter(p => !p.is_valid).length}</strong>
            </span>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
